<template>
  <template v-if="sidebarComponents.length === 0">
    <section class="layout-pt-lg layout-pb-lg" :style="`background-color: var(--c-${vars.backgroundColor})`">
      <div class="container">
        <!-- Header -->
        <div class="row justify-between items-center">
          <!-- Başlık -->
          <div class="col-auto" v-html="vars.titleHtml"></div>
          <div class="col-auto tw-hidden tw-items-center tw-space-x-4 md:tw-flex">
            <nuxt-link
              v-for="(btn, idx) in buttons"
              :key="idx"
              :to="btn.button_link"
              :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${btn.button_type}`]"
              :external="true"
            >
              {{ btn.button_label }}
            </nuxt-link>
          </div>
        </div>

        <!-- Slider -->
        <div
          class="relative mt-60 md:mt-40 sm:mt-20 js-section-slider"
          data-gap="30"
          data-slider-cols="xl-4 lg-3 md-2 sm-2 base-1"
        >
          <div class="swiper-wrapper">
            <div class="swiper-slide" v-for="(card, index) in cards" :key="index">
              <div class="imageCard -type-2 cursor-pointer" @click.prevent="handleCardClick(card)">
                <div>
                  <library-image :src="card.photo" :w-ratio="350" :h-ratio="622" :alt="card.title" />
                </div>
                <div class="tw-h-[80px]">
                  <h5 class="imageCard__title tw-line-clamp-2">{{ card.title }}</h5>
                </div>
                <div class="mt-4">
                  <button type="button" @click.stop="handleCardClick(card)" :class="`n-${card.button_type}`">
                    {{ card.button_label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Arrows -->
          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev js-prev n-link" @click="slidePrev" :disabled="!canPaginateBack">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 js-next n-link" @click="slideNext" :disabled="!canPaginateNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>

          <!-- Mobile Buttons -->
          <div class="col-auto tw-mt-8 tw-flex tw-items-center md:tw-hidden">
            <nuxt-link
              v-for="(button, idx) in buttons"
              :key="idx"
              :to="button.button_link"
              :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.button_type}`]"
              :external="true"
            >
              {{ button.button_label }}
            </nuxt-link>
          </div>
        </div>

        <!-- Popup -->
        <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
          <template #content>
            <library-form
              v-if="selectedCard.formData"
              :form="selectedCard.formData"
              version="v2"
              :component="component"
            />
          </template>
        </popup>
      </div>
    </section>
  </template>

  <template v-else-if="sidebarComponents.length > 0">
    <div
      class="tw-px-4 tw-py-9"
      :style="`background-color: var(--c-${vars.backgroundColor}); border-radius: var(--rounded); border: 1px solid var(--c-border);`"
    >
      <h4 v-html="vars.titleHtml"></h4>

      <div class="tw-relative tw-mt-10 tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-10">
        <div
          class="relative js-section-slider"
          data-anim="slide-left delay-1"
          data-gap="30"
          data-slider-cols="xl-1 lg-1 md-1 sm-1 base-1"
        >
          <Swiper
            :key="'guide-swiper-' + cards.length"
            :modules="swiperModules"
            :pagination="false"
            :slides-per-view="1"
            :space-between="20"
            effect="fade"
            :clickable="true"
            :watch-overflow="true"
            :loop="true"
            @slideChange="onSlideChange"
            @swiper="onSwiper"
            class="tw-w-full"
          >
            <SwiperSlide v-for="(card, index) in cards" :key="`ourguide-${index}`">
              <div class="imageCard -type-2 cursor-pointer" @click.prevent="handleCardClick(card)">
                <div>
                  <library-image :src="card.photo" :w-ratio="350" :h-ratio="622" :alt="card.title" />
                </div>
                <h5 class="imageCard__title">{{ card.title }}</h5>
                <div class="mt-4" v-if="card.button_label">
                  <button type="button" @click.stop="handleCardClick(card)" :class="`n-${card.button_type}`">
                    {{ card.button_label }}
                  </button>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>

          <div class="d-flex mt-60 md:mt-30 max-lg:tw-items-center max-lg:tw-justify-center">
            <button class="sliderNav -prev n-link" @click="slidePrev">
              <i class="icon-left-arrow"></i>
            </button>
            <button class="sliderNav -next ml-20 n-link" @click="slideNext">
              <i class="icon-right-arrow"></i>
            </button>
          </div>
        </div>
        <!-- Popup -->
        <popup v-if="selectedCard && popupConfig.isActive" :config="popupConfig" custom-width="90%">
          <template #content>
            <h3 class="tw-col-span-12 tw-pb-2">{{ selectedCard.title }}</h3>
            <library-form
              v-if="selectedCard.formData"
              :form="selectedCard.formData"
              version="v2"
              :is-form-name-visible="false"
              :component="component"
            />
          </template>
        </popup>
      </div>
    </div>
  </template>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import { useWebsiteStore } from '~/units/website/store';
import Popup from '~/components/common/popup/Popup.vue';
import { mapStores, mapState } from 'pinia';
import { Pagination as SwiperPagination } from 'swiper/modules';

export default defineNuxtComponent({
  name: 'CustomSliderWithPopup',
  mixins: [ComponentMixin],
  components: { Popup },

  data() {
    return {
      swiper: null,
      canPaginateBack: true,
      canPaginateNext: true,
      selectedCard: null,
      popupConfig: { isActive: false },
      swiperModules: [SwiperPagination],
    };
  },

  mounted() {
    this.onSlideChange();
  },

  computed: {
    ...mapStores(useWebsiteStore),

    sidebarComponents() {
      return this.websiteStore.sidebarComponents;
    },

    cards() {
      return this.groupedVariables.cards;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
  },

  methods: {
    handleCardClick(card: any) {
      // Always open popup for cards
      this.selectedCard = { ...card, formData: this.parseFormV2(card.guide || card.form) };
      this.popupConfig.isActive = true;
    },

    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },

    onSwiper(swiper) {
      this.swiper = swiper;
    },

    slidePrev() {
      try {
        if (this.swiper) {
          this.swiper.slidePrev();
        }
      } catch (error) {
        console.error('Error in slidePrev:', error);
      }
    },

    slideNext() {
      try {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      } catch (error) {
        console.error('Error in slideNext:', error);
      }
    },
  },
});
</script>
